import { useParams, useNavigate, Navigate } from "react-router-dom";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { Loader } from "@themes/pollinator/ui/Loader";
import {
  useFetchOrganization,
  useCanManageOrganization,
} from "@core/features/organization";
import { usePlatformStore } from "@platform/store";
import {
  OrgAdminTabs,
  OrgProfileTab,
  OrgCoworkersTabContainer,
  OrgTrackingTabContainer,
  OrgOutreachTabContainer,
  type TabValue,
} from "@platform/features/programOrgAdmin";

const VALID_TABS: TabValue[] = ["profile", "coworkers", "tracking", "outreach"];

/**
 * Organization Admin page with tabbed navigation.
 *
 * Routes:
 * - /program/:programId/organization/:orgId/admin → redirects to outreach tab
 * - /program/:programId/organization/:orgId/admin/outreach → Outreach tab
 * - /program/:programId/organization/:orgId/admin/tracking → Tracking tab
 * - /program/:programId/organization/:orgId/admin/profile → Profile tab
 * - /program/:programId/organization/:orgId/admin/coworkers → Coworkers tab
 *
 * Authorization:
 * - Page-level: Checks if user has org_manage action for this organization
 * - Backend: Also enforces authorization on all API endpoints
 */
export function OrganizationAdminPage() {
  const { programId: programIdParam, orgId, tab } = useParams<{
    programId: string;
    orgId: string;
    tab?: string;
  }>();
  const navigate = useNavigate();
  const user = usePlatformStore((state) => state.user);
  const organizationId = Number(orgId);
  const programId = Number(programIdParam);
  const isValidId = !!orgId && !isNaN(organizationId);
  const isValidProgramId = !!programIdParam && !isNaN(programId);

  // Check if user is authorized to manage this organization.
  // Passing programId scopes the actions fetch to /api/actions/<programId>/
  // (page_size=1000) instead of the default /api/actions/ (page_size=20),
  // which would silently drop the user's org_manage action when they have
  // more than 20 newer actions in the system.
  const { isAuthorized, isLoading: isAuthLoading } = useCanManageOrganization({
    organizationId,
    programId,
    user,
  });

  // Fetch organization data for the page title (only if authorized)
  const { data: organization } = useFetchOrganization({
    organizationId,
    enabled: isValidId && isAuthorized,
  });

  // Redirect to dashboard if IDs are invalid
  if (!isValidId || !isValidProgramId) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loader while checking authorization
  if (isAuthLoading) {
    return <Loader isFullPage />;
  }

  // Redirect to dashboard if not authorized
  if (!isAuthorized) {
    return <Navigate to="/dashboard" replace />;
  }

  const basePath = `/program/${programId}/organization/${organizationId}/admin`;

  // Redirect to default tab if no tab specified
  if (!tab) {
    return <Navigate to={`${basePath}/outreach`} replace />;
  }

  // Validate and set active tab
  const activeTab: TabValue = VALID_TABS.includes(tab as TabValue)
    ? (tab as TabValue)
    : "outreach";

  // Redirect to valid tab if invalid tab in URL
  if (!VALID_TABS.includes(tab as TabValue)) {
    return <Navigate to={`${basePath}/outreach`} replace />;
  }

  const handleTabChange = (newTab: TabValue) => {
    navigate(`${basePath}/${newTab}`);
  };

  const pageTitle = organization?.name
    ? `${organization.name} Admin`
    : "Organization Admin";

  return (
    <>
      <AppPageHeader
        title={pageTitle}
        description="Manage your organization settings and team"
        tabs={
          <OrgAdminTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        }
      />
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        {activeTab === "profile" && (
          <OrgProfileTab organizationId={organizationId} />
        )}

        {activeTab === "coworkers" && (
          <OrgCoworkersTabContainer
            organizationId={organizationId}
            programId={programId}
          />
        )}

        {activeTab === "tracking" && (
          <OrgTrackingTabContainer
            organizationId={organizationId}
            programId={programId}
          />
        )}

        {activeTab === "outreach" && organization && (
          <OrgOutreachTabContainer
            organization={organization}
            programId={programId}
          />
        )}
      </OuterContentContainer>
    </>
  );
}

export default OrganizationAdminPage;
